import React, { useEffect, useState } from "react"
import { SiteOption } from "../../../types/SiteOption"
import { BrandOption } from "../../../types/BrandOption"
import { BrandsData } from "../../../hooks/useBrands"
import { ModuleVOConfigScope } from "../moduleVoNavigation"
import SiteMultiSelect from "./SiteMultiSelect"
import BrandMultiSelect from "./BrandMultiSelect"

interface Props {
    scope: ModuleVOConfigScope
    disabled: boolean
    sites: SiteOption[]
    // Portée "marque" : catalogue complet + association marque -> sites (une requête par société).
    allBrandsData: BrandsData
    // Portée "site" : marques du seul site sélectionné (une requête, déclenchée à la sélection).
    siteBrands: BrandOption[]
    selectedSites: SiteOption[]
    onSitesChange: (sites: SiteOption[]) => void
    selectedBrands: BrandOption[]
    onBrandsChange: (brands: BrandOption[]) => void
}

/**
 * Portée "marque" : marques (select multiple, toujours visible) en primaire.
 * Portée "site" : sites (select multiple, toujours visible) en primaire.
 * Dans les deux cas, le second select (l'autre dimension) n'apparaît que via le bouton
 * "Configuration avancée", et uniquement tant qu'une seule entité primaire est sélectionnée :
 * dès qu'une deuxième est ajoutée, la configuration avancée se referme et se vide (pas de
 * combinaison "plusieurs marques ET plusieurs sites").
 * Portée "global" : aucun select.
 * En mode édition (disabled), les deux selects restent affichés tels quels (lecture seule).
 */
export default function ScopeSelectors({
    scope,
    disabled,
    sites,
    allBrandsData,
    siteBrands,
    selectedSites,
    onSitesChange,
    selectedBrands,
    onBrandsChange,
}: Props) {
    const [ advanced, setAdvanced ] = useState(false)

    const primaryCount = scope === 'brand' ? selectedBrands.length : selectedSites.length

    // La configuration avancée n'a de sens que pour exactement une entité primaire : dès qu'on
    // en sélectionne une deuxième (ou qu'on revient à zéro), on la referme et on la vide.
    useEffect(() => {
        if (disabled || primaryCount === 1) {
            return
        }

        setAdvanced(false)

        if (scope === 'brand' && selectedSites.length > 0) {
            onSitesChange([])
        }
        if (scope === 'site' && selectedBrands.length > 0) {
            onBrandsChange([])
        }
    }, [ primaryCount, disabled ])

    if (scope === 'global') {
        return null
    }

    const sitesForSelectedBrand = scope === 'brand' && selectedBrands.length === 1
        ? sites.filter(site => allBrandsData.siteIdsByBrand.get(selectedBrands[0].value)?.has(site.value))
        : sites

    const showAdvancedToggle = !disabled && primaryCount === 1 && !advanced
    const showSecondary = disabled || (primaryCount === 1 && advanced)

    return (
        <div className="row">
            <div className="col-md-6">
                {scope === 'brand'
                    ? <BrandMultiSelect brands={allBrandsData.brands} value={selectedBrands} onChange={onBrandsChange} disabled={disabled} />
                    : <SiteMultiSelect sites={sites} value={selectedSites} onChange={onSitesChange} disabled={disabled} />
                }
            </div>
            <div className="col-md-6 tw-flex tw-flex-col">
                {showAdvancedToggle && (
                    <div className="tw-flex tw-flex-col tw-items-center">
                        <label className="mb-1 invisible"><small>&nbsp;</small></label>
                        <button type="button" className="btn btn-outline-secondary btn-sm mb-3" onClick={() => setAdvanced(true)}>
                            Configuration avancée
                        </button>
                    </div>
                )}
                {showSecondary && (
                    scope === 'brand'
                        ? <SiteMultiSelect sites={sitesForSelectedBrand} value={selectedSites} onChange={onSitesChange} disabled={disabled} />
                        : <BrandMultiSelect brands={siteBrands} value={selectedBrands} onChange={onBrandsChange} disabled={disabled} />
                )}
            </div>
        </div>
    )
}
